import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useProjectsList } from "@/lib/useQuery/project/useProjectsList";
import { Info } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import SelectFloating from '@/components/atoms/SelectFloating';
import Combobox from '@/components/custom/Combobox';
import ErrorPopup from '@/components/custom/InfoPopups/ErrorPopup';
import SuccessPopup from "@/components/custom/InfoPopups/SuccessPopup";
import LoaderWithBackdrop from '@/components/custom/LoaderWithBackdrop';
import { useGetCrmProjectsByCrmId } from "@/features/crm_project/hooks/useQuery/useGetCrmProjectsByCrmId";
import { zodResolver } from '@hookform/resolvers/zod';
import useEditProject from '@query/project/useEditProject';
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from 'zod';

import { AddCrmProjectModal } from '@/features/crm/components/organisms/AddCrmModal';
import { useGetOrganisationCrmList } from "@/features/crm/hooks/useQuery/useGetOrganisationCrmList";

type Props = {
    show: boolean;
    onClose: () => void;
    project: Exclude<ReturnType<typeof useProjectsList>['data'], undefined>[0] | null;
}

export const ProjectCrmModal = ({
    show,
    onClose,
    project
} : Props) => {
    const [step, setStep] = useState(1);

    const navigate = useNavigate();

    const onOpenSelectedProjectCrm = () => {
        onClose();
        navigate(`/organization/${project?.organisation_id}/manage-organization?crmId=${project?.crm_id}&currentTab=crm_project&crmProjectId=${project?.crm_project_id}`);
    }

    // const onOpenNewCrmSetup = () => {

    // }

    const schema = z.object({
        crm_system: z.string().optional().nullable(),
        crm_id: z.string().optional().nullable(),
        crm_project_id: z.number().optional().nullable(),
    });

    type inputFields = z.infer<typeof schema>;
    const {
        handleSubmit,
        formState,
        reset,
        control,
        watch: surveyWatch,
        setValue,
    } = useForm<inputFields>({ resolver: zodResolver(schema)});
    const { errors } = formState;

    const selectedCRMSystem = surveyWatch("crm_system", '');
    const selectedCRM = surveyWatch("crm_id", '');
    const orgCrmBoxValue = surveyWatch("crm_project_id", null);
    const { data: crmsData } = useGetOrganisationCrmList(project?.organisation_id);
    const { data: crmProjectsData } = useGetCrmProjectsByCrmId(Number(selectedCRM));

    const [isOrgCrmBoxOpen, setIsOrgCrmBoxOpen] = useState(false);
    const setOrgCrmBoxValue = (value: string | null) => {
        setValue("crm_project_id", Number(value) || null);
    }

    useEffect(() => {
        if (selectedCRMSystem === null) {
            setValue("crm_id", null);
        }
        if (selectedCRM === null) {
            setValue("crm_project_id", null);
        }
    }, [selectedCRMSystem, setValue, selectedCRM])

    const { mutateAsync: editProject, isPending: isPendingEditProject } = useEditProject();

    useEffect(() => {
        if (show) {
            reset({
                crm_system: project?.crm_system || null,
                crm_id: String(project?.crm_id) || null,
                crm_project_id: project?.crm_project_id || null,
            })
        }
    }, [show])

    const onCloseHandler = () => {
        reset({
            crm_system: null,
            crm_id: null,
            crm_project_id: null,
        })
        setStep(1);
        onClose();
    }

    const editProjectHandler = async (data: inputFields) => {
        if (!project) {
            throw new Error("Project not found");
        }

        // TODO: Use different notyfication/toast library. Stacking of messages is needed.
        await editProject({
            id: project.id,
            crm_project_id: Number(data.crm_project_id) || null,
            crm_id: Number(data.crm_id) || null,
            crm_system: data.crm_system || null,
        })
        .then(() => {
            SuccessPopup("Project updated successfully");
            onCloseHandler();
        })
        .catch(() => {
            // TODO: Error message based on the error response
            ErrorPopup("Something went wrong while updating the project.");
        })
    };

    return (
        <>
            {(isPendingEditProject) && (<LoaderWithBackdrop />)}

            <Dialog open={show} onOpenChange={onCloseHandler}>
                <DialogContent className="">
                    <DialogHeader>
                        <DialogTitle className='m-0 font-bold text-start'>
                            Project CRM Configuration
                        </DialogTitle>
                        <DialogDescription>
                            Name: {project?.name || "-"}
                        </DialogDescription>
                    </DialogHeader>

                    <ScrollArea className="max-h-[60vh] sm:max-h-[70vh] pe-3">
                        <div className="flex flex-col gap-4 mb-3">

                            {(step === 1) && (
                                <div className="flex flex-col gap-3">
                                    { project?.crm_project?.name && (
                                        <div className="px-2 py-1 border border-green-600 rounded-md bg-green-50">
                                            <div className="flex items-center mb-1 text-green-600">
                                                <Info className="flex-shrink-0 me-1" size="16"/>
                                                <p className="text-xs">Selected project is already connected to a CRM Configuration</p>
                                            </div>

                                            <p className="text-xs"><span className="font-semibold">CRM System:</span> <span className="capitalize">{project?.crm_system || "-"}</span></p>
                                            <div>
                                                <p className="text-xs">
                                                    <span className="font-semibold me-1">CRM Project Name:</span> 
                                                    <span onClick={onOpenSelectedProjectCrm} className="text-blue-600 underline cursor-pointer">{project?.crm_project?.name || "N/A"}</span>
                                                </p>
                                                
                                            </div>
                                        </div>
                                    )}

                                    <div className="flex items-center mb-1 text-blue-600">
                                        <Info className="flex-shrink-0 me-1" size="16"/>
                                        <p className="text-xs">Select an existing CRM or create a new one</p>
                                    </div>

                                    <div className="flex flex-col items-center gap-1 px-1">

                                        <AddCrmProjectModal 
                                            label={
                                                <>
                                                    <i className="bi bi-sliders me-2"></i>Start a new CRM configuration
                                                </>
                                            }
                                            buttonClassName="w-full max-w-md"
                                            buttonVariant="outline"
                                            projects={[{
                                                id: project?.id || "",
                                                name: project?.name || "",
                                            }]}
                                            defaultStep={2}
                                        />

                                        {/* <Button size="sm" variant={'outline'} className="w-full max-w-md" onClick={onOpenNewCrmSetup}><i className="bi bi-sliders me-2"></i>Start a new CRM configuration</Button> */}
                                        <Button size="sm" variant={'outline'} className="w-full max-w-md " onClick={() => setStep(2)}><i className="bi bi-check2-all me-2"></i>Link to an existing CRM configuration</Button>
                                    
                                    </div>
                                </div>
                            )}

                            {(step === 2) && (
                                <div className="flex flex-col gap-3">
                                    <div className="flex flex-col gap-2 px-2 pt-1">
                                        <div className="m-0 mb-3">
                                            <SelectFloating
                                                label="Select CRM System (optional)"
                                                placeholder="Select CRM System"
                                                name={"crm_system"}
                                                errorMessage={errors.crm_system?.message}
                                                showError={true}
                                                control={control}
                                                dataArrayWithCustomVal={[["Salesforce", "salesforce"], ["Airtable", "airtable"]]} // TODO: get from API!
                                            />
                                        </div>
                                        <div className="m-0 mb-3">
                                            <SelectFloating
                                                disabled={!selectedCRMSystem}
                                                label="Select Organization CRM (optional)"
                                                placeholder="Select Organization CRM"
                                                name={"crm_id"}
                                                errorMessage={errors.crm_id?.message}
                                                showError={true}
                                                control={control}
                                                dataArrayWithCustomVal={crmsData?.filter((crm) => String(crm.crm_system) === String(selectedCRMSystem))?.map(crm => [crm.name, String(crm.id)])}
                                            />
                                        </div>
                                        <div className="m-0 mb-3">
                                            {(crmProjectsData) && (
                                                <>
                                                    <p className='mb-1 text-sm font-medium'>Select CRM Project (optional)</p>
                                                    <Combobox
                                                        mode="single"
                                                        items={[{ label: "Clear selection", value: "" }, ...crmProjectsData?.map((crm, i) => ({ label: `${i + 1}. ${crm.name}`, value: `${crm.id}` }))]}
                                                        setSelectedValue={setOrgCrmBoxValue}
                                                        selectedValue={orgCrmBoxValue ? String(orgCrmBoxValue) : null}
                                                        triggerLabel="Select CRM Project"

                                                        setOpen={setIsOrgCrmBoxOpen}
                                                        open={isOrgCrmBoxOpen}
                                                    />
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </ScrollArea>

                    <DialogFooter>
                     
                            <Button size="sm" variant="secondary" onClick={onCloseHandler}>Cancel</Button>

                           
                            {(step === 2) && (
                                <Button size="sm" variant="secondary" onClick={() => setStep(1)}>Back</Button>
                            )}
                            {(step === 2) && (
                                <Button size="sm" type="submit" onClick={() => handleSubmit(editProjectHandler)()}>Save</Button>
                            )}
                         
                     
                    </DialogFooter>

                </DialogContent>
            </Dialog>
        </>

    )
}
